import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createEmailCampaign,
  getEmailCampaign,
  listEmailTemplates,
  previewEmailAudience,
  sendEmailCampaign,
  sendEmailTest,
  updateEmailCampaign,
  scheduleEmailCampaign,
} from '../../../api/adminEmailCenter';
import EmailCenterPage, {
  EmptyState,
  PageSection,
  SelectField,
  StatCard,
  StatusBadge,
  TextAreaField,
  TextField,
} from './EmailCenterShared';

const steps = ['Audience', 'Content', 'Review'];

const audienceSegments = [
  { value: 'all_users', label: 'All active users' },
  { value: 'customers', label: 'Only customers' },
  { value: 'hosts', label: 'Only hosts' },
  { value: 'no_booking_yet', label: 'Users with no booking yet' },
  { value: 'top_customers', label: 'Top customers' },
];

const defaultForm = {
  campaignName: '',
  campaignType: 'promotional',
  templateId: '',
  subject: '',
  htmlBody: '<div><h1>Hello {{name}}</h1><p>Write your message here.</p><p><a href="{{unsubscribe_url}}">Unsubscribe</a></p></div>',
  textBody: '',
  audienceType: 'custom_filter',
  audienceMeta: {
    segment: 'all_users',
    city: '',
    state: '',
    minBookings: '',
    createdFrom: '',
    createdTo: '',
    manualEmails: '',
  },
};

const normalizeCampaignToForm = (item) => ({
  campaignName: item?.campaignName || '',
  campaignType: item?.campaignType || 'promotional',
  templateId: item?.templateId ? String(item.templateId) : '',
  subject: item?.subject || '',
  htmlBody: item?.htmlBody || defaultForm.htmlBody,
  textBody: item?.textBody || '',
  audienceType: item?.audienceType || 'custom_filter',
  audienceMeta: {
    segment: item?.audienceMeta?.segment || 'all_users',
    city: item?.audienceMeta?.city || '',
    state: item?.audienceMeta?.state || '',
    minBookings: item?.audienceMeta?.minBookings || '',
    createdFrom: item?.audienceMeta?.createdFrom || '',
    createdTo: item?.audienceMeta?.createdTo || '',
    manualEmails: item?.audienceMeta?.manualEmails || item?.audienceMeta?.manual_emails || '',
  },
});

export default function EmailCampaignComposer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(defaultForm);
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const templateData = await listEmailTemplates();
        if (!active) return;
        setTemplates(templateData.items || []);
      } catch (_) {
        if (!active) return;
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!id) return undefined;
    let active = true;
    (async () => {
      try {
        const data = await getEmailCampaign(id);
        if (!active) return;
        setForm(normalizeCampaignToForm(data.item));
      } catch (error) {
        if (!active) return;
        setError(error.message || 'Failed to load campaign');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const selectedTemplate = useMemo(
    () => templates.find((item) => String(item.id) === String(form.templateId || '')),
    [form.templateId, templates]
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    setForm((current) => ({
      ...current,
      subject: current.subject || selectedTemplate.subject,
      htmlBody: current.htmlBody === defaultForm.htmlBody ? selectedTemplate.htmlBody : current.htmlBody,
      textBody: current.textBody || selectedTemplate.textBody || '',
    }));
  }, [selectedTemplate]);

  const runAudiencePreview = async () => {
    setLoadingPreview(true);
    setError('');
    try {
      const data = await previewEmailAudience({
        audienceType: form.audienceType,
        audienceMeta: {
          ...form.audienceMeta,
          manualEmails: form.audienceType === 'manual_selection' ? form.audienceMeta.manualEmails : undefined,
        },
      });
      setPreview(data.preview);
      setStep((current) => Math.max(current, 1));
    } catch (error) {
      setError(error.message || 'Audience preview failed');
    } finally {
      setLoadingPreview(false);
    }
  };

  const buildPayload = () => ({
    campaignName: form.campaignName,
    campaignType: form.campaignType,
    templateId: form.templateId ? Number(form.templateId) : null,
    subject: form.subject,
    htmlBody: form.htmlBody,
    textBody: form.textBody || null,
    audienceType: form.audienceType,
    audienceMeta: {
      ...form.audienceMeta,
      minBookings: form.audienceMeta.minBookings ? Number(form.audienceMeta.minBookings) : undefined,
      manualEmails: form.audienceType === 'manual_selection' ? form.audienceMeta.manualEmails : undefined,
    },
  });

  const saveDraft = async () => {
    setSaving(true);
    setError('');
    setInfo('');
    try {
      const payload = buildPayload();
      const response = id ? await updateEmailCampaign(id, payload) : await createEmailCampaign(payload);
      const nextId = response.item?.id || id;
      setInfo('Campaign draft saved.');
      if (!id && nextId) {
        navigate(`/admin/email/campaigns/${nextId}`, { replace: true });
      }
    } catch (error) {
      setError(error.message || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail) {
      setError('Enter a test email address first.');
      return;
    }
    setTesting(true);
    setError('');
    setInfo('');
    try {
      await sendEmailTest({
        recipientEmail: testEmail,
        subject: form.subject,
        htmlBody: form.htmlBody,
        textBody: form.textBody || '',
        variables: {
          name: 'Preview User',
          first_name: 'Preview',
          email: testEmail,
          campaign_name: form.campaignName || 'Preview Campaign',
          unsubscribe_url: 'https://www.homevilas.com',
          support_email: 'info@homevilas.com',
        },
      });
      setInfo(`Test email sent to ${testEmail}.`);
    } catch (error) {
      setError(error.message || 'Test email failed');
    } finally {
      setTesting(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    setError('');
    setInfo('');
    try {
      let campaignId = id;
      if (!campaignId) {
        const created = await createEmailCampaign(buildPayload());
        campaignId = created.item?.id;
      } else {
        await updateEmailCampaign(campaignId, buildPayload());
      }
      await sendEmailCampaign(campaignId);
      navigate('/admin/email/campaigns');
    } catch (error) {
      setError(error.message || 'Failed to queue campaign');
    } finally {
      setSending(false);
    }
  };

  const scheduleSend = async () => {
    const when = window.prompt('Enter schedule datetime in YYYY-MM-DDTHH:mm format');
    if (!when) return;
    setSending(true);
    setError('');
    try {
      let campaignId = id;
      if (!campaignId) {
        const created = await createEmailCampaign(buildPayload());
        campaignId = created.item?.id;
      } else {
        await updateEmailCampaign(campaignId, buildPayload());
      }
      await scheduleEmailCampaign(campaignId, { scheduledAt: when });
      navigate('/admin/email/campaigns');
    } catch (error) {
      setError(error.message || 'Failed to schedule campaign');
    } finally {
      setSending(false);
    }
  };

  return (
    <EmailCenterPage
      title={id ? 'Edit Campaign' : 'Compose Campaign'}
      description="Audience-first campaign builder with preview counts, test sends, scheduling, and production-safe queueing."
      actions={
        <>
          <button type="button" onClick={saveDraft} disabled={saving} className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700">
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button type="button" onClick={scheduleSend} disabled={sending} className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700">
            Schedule
          </button>
          <button type="button" onClick={sendNow} disabled={sending} className="rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_32px_rgba(15,118,110,0.24)]">
            {sending ? 'Queueing...' : 'Send Now'}
          </button>
        </>
      }
    >
      {error ? <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {info ? <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{info}</div> : null}

      <div className="grid gap-4 xl:grid-cols-4">
        <StatCard label="Step" value={`${step + 1}/3`} hint={steps[step]} />
        <StatCard label="Audience Count" value={preview?.totalRecipients ?? 0} hint="Run preview before send." tone="sky" />
        <StatCard label="Skipped" value={preview?.skipped?.length ?? 0} hint="Unsubscribed or suppressed." tone="amber" />
        <StatCard label="Campaign State" value={id ? 'Existing' : 'New'} hint={id ? 'Editing a saved draft or campaign.' : 'Draft not yet persisted.'} />
      </div>

      <div className="flex flex-wrap gap-2">
        {steps.map((label, index) => (
          <button
            key={label}
            type="button"
            onClick={() => setStep(index)}
            className={`rounded-full border px-4 py-2 text-sm font-semibold ${step === index ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-200 bg-white text-slate-600'}`}
          >
            {index + 1}. {label}
          </button>
        ))}
      </div>

      {loading ? (
        <EmptyState title="Loading campaign" body="Campaign details are being prepared." />
      ) : (
        <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-6">
            <PageSection title="Step 1: Audience" subtitle="Define who should receive this campaign and preview the count before send.">
              <div className="grid gap-4 md:grid-cols-2">
                <TextField label="Campaign Name" value={form.campaignName} onChange={(e) => setForm((current) => ({ ...current, campaignName: e.target.value }))} />
                <SelectField label="Campaign Type" value={form.campaignType} onChange={(e) => setForm((current) => ({ ...current, campaignType: e.target.value }))}>
                  <option value="promotional">Promotional</option>
                  <option value="announcement">Announcement</option>
                  <option value="offer">Offer</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="manual">Manual</option>
                </SelectField>
                <SelectField
                  label="Audience Mode"
                  value={form.audienceType}
                  onChange={(e) => setForm((current) => ({ ...current, audienceType: e.target.value }))}
                >
                  <option value="custom_filter">Segmented Audience</option>
                  <option value="manual_selection">Manual Email List</option>
                </SelectField>
                {form.audienceType === 'custom_filter' ? (
                  <SelectField
                    label="Audience Segment"
                    value={form.audienceMeta.segment}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        audienceMeta: { ...current.audienceMeta, segment: e.target.value },
                      }))
                    }
                  >
                    {audienceSegments.map((segment) => (
                      <option key={segment.value} value={segment.value}>
                        {segment.label}
                      </option>
                    ))}
                  </SelectField>
                ) : null}
                {form.audienceType === 'custom_filter' ? (
                  <>
                    <TextField
                      label="City"
                      value={form.audienceMeta.city}
                      onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, city: e.target.value } }))}
                    />
                    <TextField
                      label="State"
                      value={form.audienceMeta.state}
                      onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, state: e.target.value } }))}
                    />
                    <TextField
                      label="Min Bookings"
                      type="number"
                      value={form.audienceMeta.minBookings}
                      onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, minBookings: e.target.value } }))}
                    />
                    <TextField
                      label="Created From"
                      type="date"
                      value={form.audienceMeta.createdFrom}
                      onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, createdFrom: e.target.value } }))}
                    />
                    <TextField
                      label="Created To"
                      type="date"
                      value={form.audienceMeta.createdTo}
                      onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, createdTo: e.target.value } }))}
                    />
                  </>
                ) : (
                  <div className="md:col-span-2">
                    <TextAreaField
                      label="Manual Emails"
                      placeholder="one@example.com&#10;two@example.com"
                      value={form.audienceMeta.manualEmails}
                      onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, manualEmails: e.target.value } }))}
                    />
                  </div>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <button type="button" onClick={runAudiencePreview} disabled={loadingPreview} className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white">
                  {loadingPreview ? 'Calculating...' : 'Preview Audience'}
                </button>
                {preview ? <StatusBadge value={`${preview.totalRecipients} recipients`} /> : null}
              </div>
            </PageSection>

            <PageSection title="Step 2: Content" subtitle="Choose an existing template or compose custom HTML with placeholder support.">
              <div className="grid gap-4 md:grid-cols-2">
                <SelectField
                  label="Template"
                  value={form.templateId}
                  onChange={(e) => setForm((current) => ({ ...current, templateId: e.target.value }))}
                >
                  <option value="">Compose custom email</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </SelectField>
                <TextField label="Subject" value={form.subject} onChange={(e) => setForm((current) => ({ ...current, subject: e.target.value }))} />
                <div className="md:col-span-2">
                  <TextAreaField
                    label="HTML Body"
                    value={form.htmlBody}
                    onChange={(e) => setForm((current) => ({ ...current, htmlBody: e.target.value }))}
                    className="min-h-[320px] font-mono"
                  />
                </div>
                <div className="md:col-span-2">
                  <TextAreaField
                    label="Plain Text Fallback"
                    value={form.textBody}
                    onChange={(e) => setForm((current) => ({ ...current, textBody: e.target.value }))}
                    className="min-h-[160px] font-mono"
                  />
                </div>
              </div>
              <div className="mt-4 flex flex-col gap-3 md:flex-row">
                <TextField label="Send Test Email To" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
                <div className="md:self-end">
                  <button type="button" onClick={sendTest} disabled={testing} className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700">
                    {testing ? 'Sending Test...' : 'Send Test Email'}
                  </button>
                </div>
              </div>
            </PageSection>
          </div>

          <div className="space-y-6">
            <PageSection title="Step 3: Review" subtitle="Validate the recipient sample, placeholder support, and final content before queueing.">
              {!preview ? (
                <EmptyState title="Audience preview pending" body="Run audience preview to unlock send readiness, skipped contacts, and sample recipients." />
              ) : (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <StatCard label="Recipients" value={preview.totalRecipients} hint="Estimated send count" tone="emerald" />
                    <StatCard label="Suppressed" value={preview.skipped?.length || 0} hint="Unsubscribed or blocked" tone="amber" />
                  </div>
                  <div className="rounded-[24px] border border-slate-100 bg-slate-50/70 p-4">
                    <p className="text-sm font-semibold text-slate-900">Sample recipients</p>
                    <div className="mt-3 space-y-2">
                      {(preview.sampleRecipients || []).map((recipient) => (
                        <div key={recipient.email} className="flex items-center justify-between rounded-2xl border border-white bg-white px-4 py-3 text-sm">
                          <div>
                            <p className="font-medium text-slate-900">{recipient.fullName || 'Guest'}</p>
                            <p className="text-slate-500">{recipient.email}</p>
                          </div>
                          <span className="text-xs uppercase tracking-[0.18em] text-slate-400">{recipient.metadata?.entityType || recipient.sourceType}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-[24px] border border-slate-100 bg-slate-50/70 p-4">
                    <p className="text-sm font-semibold text-slate-900">Subject Preview</p>
                    <p className="mt-2 text-sm text-slate-600">{form.subject || 'No subject yet'}</p>
                  </div>
                </div>
              )}
            </PageSection>

            <PageSection title="Live HTML Preview" subtitle="Rendered preview with placeholder examples.">
              <div className="rounded-[24px] border border-slate-100 bg-slate-50/50 p-4">
                <div
                  className="prose max-w-none text-sm"
                  dangerouslySetInnerHTML={{
                    __html: form.htmlBody
                      .replace(/\{\{name\}\}/g, 'Preview User')
                      .replace(/\{\{first_name\}\}/g, 'Preview')
                      .replace(/\{\{email\}\}/g, 'preview@homevilas.com')
                      .replace(/\{\{campaign_name\}\}/g, form.campaignName || 'Preview Campaign')
                      .replace(/\{\{support_email\}\}/g, 'info@homevilas.com')
                      .replace(/\{\{unsubscribe_url\}\}/g, '#'),
                  }}
                />
              </div>
            </PageSection>
          </div>
        </div>
      )}
    </EmailCenterPage>
  );
}
